import React, { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import Navbar from '../navbar/Navbar';
import axios from 'axios';
import iconGroup from '../assets/Icongroup.svg';
import iconAdmin from '../assets/Iconadmin.svg';
import setting from '../assets/setting.svg';
import Pagination from '../pagination/Pagination';
import deleteIcon from '../assets/delete.svg';
import resetPassword from '../assets/mdp_restore.svg';
import addCircleIcon from '../assets/add-circle.svg';

const addCircle = { path: addCircleIcon };

interface RegisterFormData {
    email: string;
    password: string;
    groupement: {
        id: string;
        [key: string]: any;
    };
}

function Administration() {
    const [isGroupOpen, setIsGroupOpen] = useState(false);
    const [isAdminOpen, setIsAdminOpen] = useState(false);
    const [isSettingAdminOpen, setIsSettingAdminOpen] = useState(false);
    const [isSettingGroupOpen, setIsSettingGroupOpen] = useState(false);
    const [groupement, setGroupement] = useState<any[]>([]);
    const [admins, setAdmins] = useState<any[]>([]);
    const [name, setName] = useState('');
    const [isGlobal, setIsGlobal] = useState(false);
    const [flashMessageGroup, setFlashMessageGroup] = useState('');
    const [openFlashGroup, setOpenFlashGroup] = useState(false);
    const [flashMessageAdmin, setFlashMessageAdmin] = useState('');
    const [openFlashAdmin, setOpenFlashAdmin] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [formData, setFormData] = useState<RegisterFormData>({
        email: '',
        password: '',
        groupement: {
            id: '',
        },
    });
    const isFetching = useRef(false);
    let itemsPerPage = 3;

    const handleGroupHeaderClick = () => {
        setIsGroupOpen(!isGroupOpen);
        setIsAdminOpen(false);
        setIsSettingAdminOpen(false);
        setIsSettingGroupOpen(false);
    };

    const handleAdminHeaderClick = () => {
        setIsAdminOpen(!isAdminOpen);
        setIsGroupOpen(false);
        setIsSettingAdminOpen(false);
        setIsSettingGroupOpen(false);
    };

    const handleSettingAdminHeaderClick = () => {
        setIsSettingAdminOpen(!isSettingAdminOpen);
        setIsGroupOpen(false);
        setIsAdminOpen(false);
        setIsSettingGroupOpen(false);
    };

    const handleSettingGroupHeaderClick = () => {
        setIsSettingGroupOpen(!isSettingGroupOpen);
        setIsGroupOpen(false);
        setIsAdminOpen(false);
        setIsSettingAdminOpen(false);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name === 'groupement') {
            setFormData(prev => ({ ...prev, groupement: { id: value } }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleCreateGroupement = async (e: FormEvent) => {
        e.preventDefault();
        const resp = await axios.post(
            '/api/groupements',
            {
                name: name,
                isGlobal: isGlobal,
            },
            {
                headers: {
                    'Content-Type': 'application/ld+json',
                },
            },
        );

        if (resp.status === 201) {
            setName('');
            setIsGlobal(false);
            setOpenFlashGroup(true);
            setFlashMessageGroup('Groupement créé avec succès');
            await fetchGroupement();
            setTimeout(() => {
                setOpenFlashGroup(false);
                setFlashMessageGroup('');
            }, 8000);
        } else {
            setOpenFlashGroup(true);
            setFlashMessageGroup('Echec de création du groupement');
            setTimeout(() => {
                setOpenFlashGroup(false);
                setFlashMessageGroup('');
            }, 8000);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/register', {
                email: formData.email,
                password: formData.password,
                groupement: `/api/groupements/${formData.groupement.id}`,
            });

            if (response.data.message) {
                setOpenFlashAdmin(true);
                setFlashMessageAdmin('Admin créé avec succès');
                setFormData({
                    email: '',
                    password: '',
                    groupement: {
                        id: '',
                    },
                });
                setTimeout(() => {
                    setOpenFlashAdmin(false);
                    setFlashMessageAdmin('');
                }, 8000);
            } else {
                setOpenFlashAdmin(true);
                setFlashMessageAdmin("Echec de création de l'admin");
                setTimeout(() => {
                    setOpenFlashAdmin(false);
                    setFlashMessageAdmin('');
                }, 8000);
            }
        } catch (error) {
            console.error('Error registering new admin:', error);
            setOpenFlashAdmin(true);
            setFlashMessageAdmin(
                'Le mot de passe doit comporter au moins 6 caractères et contenir au moins un chiffre, une lettre majuscule et une lettre minuscule.',
            );
            setTimeout(() => {
                setOpenFlashAdmin(false);
                setFlashMessageAdmin('');
            }, 8000);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await axios.delete(`/api/groupements/${id}`);
            await fetchGroupement();
        } catch (error) {
            console.error('Error when deleting groupement : ' + error);
        }
    };

    const handleDeleteAdmin = async (id: string) => {
        try {
            await axios.delete(`/api/user_admins/${id}`);
            await fetchAdmins();
        } catch (error) {
            console.error('Error when deleting user Admin : ' + error);
        }
    };

    const handleResetPassword = async (email: string) => {
        try {
            const formData = new FormData();
            formData.append('email', email);

            await axios.post('/reset-password', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        } catch (error) {
            console.error('Error when refresh password request ' + error);
        }
    };

    const fetchGroupement = async () => {
        try {
            const response = await axios.get('/api/groupements');
            setGroupement(response.data['hydra:member']);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchGroupement();
    }, []);

    const fetchAdmins = useCallback(async () => {
        if (isFetching.current) {
            return;
        }

        try {
            isFetching.current = true;
            const response = await axios.get(
                `/api/user_admins?itemsPerPage=${itemsPerPage}&page=${currentPage}`,
            );
            setAdmins(response.data['hydra:member']);
            setTotalItems(response.data['hydra:totalItems']);
            const total = Math.ceil(totalItems / itemsPerPage);
            setTotalPage(total > 0 ? total : 1);
        } catch (error) {
            console.error('Error when fetching Admins : ' + error);
        } finally {
            isFetching.current = false;
        }
    }, [currentPage, itemsPerPage, totalItems]);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    return (
        <>
            <div className="flex flex-1 relative w-full h-screen">
                <div className="flex-none relative min-w-64">
                    <Navbar />
                </div>
                <div className="flex-1 relative">
                    <div className="flex flex-col items-start relative h-full">
                        <div className="flex flex-col py-10 px-9 w-full">
                            <div className="flex items-center justify-between w-full pb-8 border-b border-light-gray">
                                <div className="flex flex-col items-start gap-1 relative">
                                    <h1 className="text-dark-gray text-super-heavy font-heavy m-0 p-0">
                                        Administration
                                    </h1>
                                    <p className="text-gray m-0 p-0">
                                        Vous avez la possibilité d’administrer les groupements et
                                        administrateurs.
                                    </p>
                                </div>
                            </div>
                            <div
                                key="groupement"
                                className="flex flex-col bg-white p-0 px-5 rounded-xl mb-6 shadow-lg transition-all duration-300 ease-in-out overflow-hidden relative h-auto"
                            >
                                <div className="">
                                    <h2
                                        className={'text-heavy font-heavy'}
                                        onClick={handleGroupHeaderClick}
                                    >
                                        <img
                                            className={'mr-3'}
                                            src={iconGroup}
                                            style={{
                                                width: 32,
                                                height: 32,
                                            }}
                                            alt="logo groupement"
                                        />
                                        Créer un groupement
                                    </h2>
                                </div>
                                <div>
                                    {isGroupOpen && (
                                        <>
                                            <form onSubmit={handleCreateGroupement}>
                                                <label htmlFor="name">{`Nom du groupement`}</label>
                                                <div className="flex justify-between gap-6 mb-[30px]">
                                                    <input
                                                        className={'w-[40%] text-small'}
                                                        type="text"
                                                        name="name"
                                                        value={name}
                                                        onChange={e => setName(e.target.value)}
                                                        placeholder={`Nom du groupement`}
                                                        required
                                                    />
                                                    <button className="ml-2 flex leading-6 rounded-[1.5rem] w-fit text-medium font-medium border border-primary bg-primary items-center content-center px-[10px] py-[10px] text-dark-gray flex-row justify-center">
                                                        <img
                                                            src={addCircle.path}
                                                            className="mr-1"
                                                            alt="add circle icon"
                                                        />
                                                        Créer un groupement
                                                    </button>
                                                </div>
                                            </form>
                                            {openFlashGroup && <p>{flashMessageGroup}</p>}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div
                                key="new-admin"
                                className="flex flex-col bg-white p-0 px-5 rounded-xl mb-6 shadow-lg transition-all duration-300 ease-in-out overflow-hidden relative h-auto"
                            >
                                <div className="">
                                    <h2
                                        className={'text-heavy font-heavy'}
                                        onClick={handleAdminHeaderClick}
                                    >
                                        <img
                                            className={'mr-3'}
                                            src={iconAdmin}
                                            style={{
                                                width: 32,
                                                height: 32,
                                            }}
                                            alt="logo publication"
                                        />
                                        Créer un administrateur
                                    </h2>
                                </div>
                                <div className="flex justify-between w-full">
                                    {isAdminOpen && (
                                        <form onSubmit={handleSubmit} className="w-full">
                                            <div className="w-full flex flex-1">
                                                <div className="p-[10px] text-medium h-auto">
                                                    <label htmlFor="email">Email</label>
                                                    <input
                                                        type="email"
                                                        id="email"
                                                        name="email"
                                                        value={formData.email}
                                                        className="block leading-[1.5] bg-generic-white rounded-[4px] border border-[#ced4da] [width:calc(50vw-200px)] text-small"
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="p-[10px] text-medium h-auto">
                                                    <label htmlFor="password">Mot de passe</label>
                                                    <input
                                                        type="password"
                                                        id="password"
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleChange}
                                                        className="block leading-[1.5] bg-generic-white rounded-[4px] border border-[#ced4da] [width:calc(50vw-200px)] text-small"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="p-[10px] text-medium h-auto">
                                                <label htmlFor="groupement">Groupement</label>
                                            </div>
                                            <div className="flex justify-between gap-6 mb-[30px]">
                                                <div className="p-[10px] text-medium h-auto text-small">
                                                    <select
                                                        name={'groupement'}
                                                        className={'[width:calc(50vw-200px)]'}
                                                        onChange={handleChange}
                                                        value={formData.groupement.id}
                                                    >
                                                        <option value=""></option>
                                                        {groupement.map((result, index) => (
                                                            <option key={index} value={result.id}>
                                                                {result.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="p-[10px] text-medium h-auto">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-block add-circle"
                                                    >
                                                        Créer un administrateur
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    )}
                                </div>
                                {openFlashAdmin && <p>{flashMessageAdmin}</p>}
                            </div>
                            <div
                                key="setting-admin"
                                className="flex flex-col bg-white p-0 px-5 rounded-xl mb-6 shadow-lg transition-all duration-300 ease-in-out overflow-hidden relative h-auto"
                            >
                                <div className="">
                                    <h2
                                        className={'text-heavy font-heavy'}
                                        onClick={handleSettingAdminHeaderClick}
                                    >
                                        <img
                                            className={'mr-3'}
                                            src={setting}
                                            style={{
                                                width: 32,
                                                height: 32,
                                                filter: 'grayscale(1) brightness(0.5)',
                                            }}
                                            alt="logo publication"
                                        />
                                        Gestion des administrateurs
                                    </h2>
                                </div>
                                <div>
                                    {isSettingAdminOpen && (
                                        <>
                                            <div className="min-w-full">
                                                <table className="w-full border-collapse mt-5">
                                                    <tbody>
                                                        {admins.map((result, index) => (
                                                            <tr key={index}>
                                                                <td className="p-2.5 border-b border-gray-300 border-[#ddd] text-left">
                                                                    <div className="flex items-center justify-start px-3 py-2">
                                                                        {result.email}
                                                                    </div>
                                                                </td>
                                                                <td className="p-2.5 border-b border-gray-300 border-[#ddd] text-left">
                                                                    <div className="flex items-center justify-start px-3 py-2">
                                                                        {result.groupement?.name}
                                                                    </div>
                                                                </td>
                                                                <td className="p-2.5 border-b border-gray-300 border-[#ddd] text-left">
                                                                    <div className="flex items-center justify-start px-3 py-2">
                                                                        {result.quotaIA}
                                                                    </div>
                                                                </td>
                                                                <td className="p-2.5 border-b border-gray-300 border-[#ddd] text-left">
                                                                    <div className="flex items-center justify-start px-3 py-2">
                                                                        {result.requestIA}
                                                                    </div>
                                                                </td>
                                                                <td className="p-2.5 border-b border-gray-300 border-[#ddd] text-left">
                                                                    <div className="flex items-center justify-start px-3 py-2">
                                                                        <button
                                                                            className="flex border-none font-montserrat text-small cursor-pointer block text-black px-4 py-2 leading-[1.5] rounded-[1.5rem] grayscale bg-primary-light"
                                                                            onClick={() =>
                                                                                handleResetPassword(
                                                                                    result.email,
                                                                                )
                                                                            }
                                                                        >
                                                                            <img
                                                                                className={'mr-1'}
                                                                                src={resetPassword}
                                                                                style={{
                                                                                    width: 15,
                                                                                    height: 15,
                                                                                }}
                                                                                alt="logo reset password"
                                                                            />{' '}
                                                                            Réinitialisation MDP
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                                <td className="w-px whitespace-nowrap p-2.5 border-b border-gray-300 border-[#ddd] text-left">
                                                                    <div className="flex items-center justify-start px-3 py-2 right">
                                                                        <button
                                                                            className="flex items-center justify-center text-black px-4 py-2 text-small leading-[1.5] rounded-[1.5rem] bg-status-error"
                                                                            onClick={() =>
                                                                                handleDeleteAdmin(
                                                                                    result.id,
                                                                                )
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={deleteIcon}
                                                                                style={{
                                                                                    width: 10,
                                                                                    height: 10,
                                                                                }}
                                                                                alt="logo delete"
                                                                            />{' '}
                                                                            Supprimer
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <Pagination
                                                currentPage={currentPage}
                                                totalPage={totalPage}
                                                previous={() => setCurrentPage(p => p - 1)}
                                                next={() => setCurrentPage(p => p + 1)}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            <div
                                key="setting-group"
                                className="flex flex-col bg-white p-0 px-5 rounded-xl mb-6 shadow-lg transition-all duration-300 ease-in-out overflow-hidden relative h-auto"
                            >
                                <div className="">
                                    <h2
                                        className={'text-heavy font-heavy'}
                                        onClick={handleSettingGroupHeaderClick}
                                    >
                                        <img
                                            className={'mr-3'}
                                            src={setting}
                                            style={{
                                                width: 32,
                                                height: 32,
                                                filter: 'grayscale(1) brightness(0.5)',
                                            }}
                                            alt="logo publication"
                                        />
                                        Gestion des groupements
                                    </h2>
                                </div>
                                <div>
                                    {isSettingGroupOpen && (
                                        <div className="min-w-full">
                                            <table className="w-full border-collapse mt-5">
                                                <tbody>
                                                    {groupement.map((result, index) => (
                                                        <tr key={index}>
                                                            <td className="p-2.5 border-b border-gray-300 border-[#ddd] text-left">
                                                                {result.name}
                                                            </td>
                                                            <td
                                                                className={
                                                                    'w-px whitespace-nowrap p-2.5 border-b border-gray-300 border-[#ddd] text-left'
                                                                }
                                                            >
                                                                <button
                                                                    className={
                                                                        'flex items-center justify-center text-black px-4 py-2 text-small leading-[1.5] rounded-[1.5rem] bg-status-error'
                                                                    }
                                                                    onClick={() =>
                                                                        handleDelete(result.id)
                                                                    }
                                                                >
                                                                    <img
                                                                        src={deleteIcon}
                                                                        style={{
                                                                            width: 10,
                                                                            height: 10,
                                                                        }}
                                                                        alt="logo delete"
                                                                    />{' '}
                                                                    Supprimer
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Administration;
